/**
 * AvailabilityManager - Reusable component for managing room/teacher availability
 *
 * Manages availability slots for rooms or teachers with CRUD operations
 * Supports multiple slots per day and quick template filling
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Alert,
  Divider,
  CircularProgress,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import {
  useGetRoomAvailabilitiesQuery,
  useCreateRoomAvailabilityMutation,
  useUpdateRoomAvailabilityMutation,
  useDeleteRoomAvailabilityMutation,
  useGetTeacherAvailabilitiesQuery,
  useCreateTeacherAvailabilityMutation,
  useUpdateTeacherAvailabilityMutation,
  useDeleteTeacherAvailabilityMutation,
} from '../../../api/schedulingApi';
import type {
  RoomAvailability,
  TeacherAvailability,
} from '../../../types/scheduling';

interface AvailabilityManagerProps {
  open: boolean;
  onClose: () => void;
  entityType: 'room' | 'teacher';
  entityId: string;
  entityName: string;
  studioId: string;
}

type Availability = RoomAvailability | TeacherAvailability;

const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

const AvailabilityManager: React.FC<AvailabilityManagerProps> = ({
  open,
  onClose,
  entityType,
  entityId,
  entityName,
  studioId,
}) => {
  // Conditional hooks based on entity type
  const {
    data: roomAvailabilities,
    isLoading: isLoadingRoom,
    error: roomError,
  } = useGetRoomAvailabilitiesQuery(
    { roomId: entityId, studioId },
    { skip: entityType !== 'room' }
  );

  const {
    data: teacherAvailabilities,
    isLoading: isLoadingTeacher,
    error: teacherError,
  } = useGetTeacherAvailabilitiesQuery(
    { teacherId: entityId, studioId },
    { skip: entityType !== 'teacher' }
  );

  const [createRoomAvailability, { isLoading: isCreatingRoom }] =
    useCreateRoomAvailabilityMutation();
  const [updateRoomAvailability, { isLoading: isUpdatingRoom }] =
    useUpdateRoomAvailabilityMutation();
  const [deleteRoomAvailability, { isLoading: isDeletingRoom }] =
    useDeleteRoomAvailabilityMutation();

  const [createTeacherAvailability, { isLoading: isCreatingTeacher }] =
    useCreateTeacherAvailabilityMutation();
  const [updateTeacherAvailability, { isLoading: isUpdatingTeacher }] =
    useUpdateTeacherAvailabilityMutation();
  const [deleteTeacherAvailability, { isLoading: isDeletingTeacher }] =
    useDeleteTeacherAvailabilityMutation();

  // Form state
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<Availability | null>(null);
  const [day, setDay] = useState<string>('Monday');
  const [startTime, setStartTime] = useState<string>('09:00');
  const [endTime, setEndTime] = useState<string>('17:00');
  const [formError, setFormError] = useState<string>('');

  // Get the appropriate data and loading states
  const availabilities =
    entityType === 'room' ? roomAvailabilities : teacherAvailabilities;
  const isLoading = entityType === 'room' ? isLoadingRoom : isLoadingTeacher;
  const error = entityType === 'room' ? roomError : teacherError;
  const isCreating = entityType === 'room' ? isCreatingRoom : isCreatingTeacher;
  const isUpdating = entityType === 'room' ? isUpdatingRoom : isUpdatingTeacher;
  const isDeleting = entityType === 'room' ? isDeletingRoom : isDeletingTeacher;

  // Group availabilities by day
  const groupedAvailabilities = useMemo(() => {
    if (!availabilities) return {};

    const grouped: Record<string, Availability[]> = {};
    availabilities.forEach((slot: Availability) => {
      if (!grouped[slot.day]) {
        grouped[slot.day] = [];
      }
      grouped[slot.day].push(slot);
    });

    // Sort slots within each day by start time
    Object.keys(grouped).forEach(dayKey => {
      grouped[dayKey].sort((a, b) => a.start_time.localeCompare(b.start_time));
    });

    return grouped;
  }, [availabilities]);

  const resetForm = () => {
    setDay('Monday');
    setStartTime('09:00');
    setEndTime('17:00');
    setFormError('');
    setEditingSlot(null);
  };

  const validateTime = (): boolean => {
    if (startTime >= endTime) {
      setFormError('End time must be after start time');
      return false;
    }
    setFormError('');
    return true;
  };

  const handleOpenAddForm = () => {
    resetForm();
    setIsAddFormOpen(true);
  };

  const handleCloseAddForm = () => {
    setIsAddFormOpen(false);
    resetForm();
  };

  const handleOpenEdit = (slot: Availability) => {
    setEditingSlot(slot);
    setDay(slot.day);
    setStartTime(slot.start_time);
    setEndTime(slot.end_time);
    setFormError('');
    setIsAddFormOpen(true);

    // Scroll to form after state update
    setTimeout(() => {
      const formElement = document.querySelector('[data-form-section="true"]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleAddSlot = async () => {
    if (!validateTime()) return;

    try {
      if (entityType === 'room') {
        await createRoomAvailability({
          studioId,
          data: {
            room: entityId,
            day,
            start_time: startTime,
            end_time: endTime,
          },
        }).unwrap();
      } else {
        await createTeacherAvailability({
          studioId,
          data: {
            teacher: entityId,
            day,
            start_time: startTime,
            end_time: endTime,
          },
        }).unwrap();
      }

      handleCloseAddForm();
    } catch (err) {
      console.error('Failed to create availability:', err);
      setFormError('Failed to create availability slot');
    }
  };

  const handleUpdateSlot = async () => {
    if (!editingSlot || !validateTime()) return;

    try {
      const data = {
        day,
        start_time: startTime,
        end_time: endTime,
      };

      if (entityType === 'room') {
        await updateRoomAvailability({
          id: editingSlot.id,
          studioId,
          data: { room: entityId, ...data },
        }).unwrap();
      } else {
        await updateTeacherAvailability({
          id: editingSlot.id,
          studioId,
          data: { teacher: entityId, ...data },
        }).unwrap();
      }

      handleCloseAddForm();
    } catch (err) {
      console.error('Failed to update availability:', err);
      setFormError('Failed to update availability slot');
    }
  };

  const handleDelete = async (slot: Availability) => {
    try {
      if (entityType === 'room') {
        await deleteRoomAvailability({
          id: slot.id,
          studioId,
          roomId: entityId,
        }).unwrap();
      } else {
        await deleteTeacherAvailability({
          id: slot.id,
          studioId,
          teacherId: entityId,
        }).unwrap();
      }
    } catch (err) {
      console.error('Failed to delete availability:', err);
    }
  };

  const handleQuickTemplate = async () => {
    const templateDays = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];

    try {
      const promises = templateDays.map(templateDay => {
        if (entityType === 'room') {
          return createRoomAvailability({
            studioId,
            data: {
              room: entityId,
              day: templateDay,
              start_time: '09:00',
              end_time: '21:00',
            },
          }).unwrap();
        }
        return createTeacherAvailability({
          studioId,
          data: {
            teacher: entityId,
            day: templateDay,
            start_time: '09:00',
            end_time: '21:00',
          },
        }).unwrap();
      });

      await Promise.all(promises);
    } catch (err) {
      console.error('Failed to apply template:', err);
    }
  };

  // Format time for display (convert 24h to 12h)
  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    let displayHour = hour;
    if (hour === 0) {
      displayHour = 12;
    } else if (hour > 12) {
      displayHour = hour - 12;
    }
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{entityName} Availability</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Quick Template Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Quick Fill Template
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AutoFixHighIcon />}
              onClick={handleQuickTemplate}
              disabled={isCreating}
            >
              Fill Mon-Sat 9am-9pm
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Adds availability for Monday through Saturday, 9:00 AM to 9:00 PM
            </Typography>
          </Box>

          <Divider />

          {/* Current Availability Display */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Current Availability
            </Typography>

            {isLoading && (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!isLoading && error && (
              <Alert severity="error">Failed to load availability</Alert>
            )}
            {!isLoading &&
              !error &&
              (!availabilities || availabilities.length === 0) && (
                <Alert severity="info">
                  No availability slots set. Add slots below to define when this{' '}
                  {entityType} is available.
                </Alert>
              )}
            {!isLoading &&
              !error &&
              availabilities &&
              availabilities.length > 0 && (
                <List>
                  {DAYS_OF_WEEK.map(dayOfWeek => {
                    const slots = groupedAvailabilities[dayOfWeek];
                    if (!slots || slots.length === 0) return null;

                    return (
                      <React.Fragment key={dayOfWeek}>
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" fontWeight="bold">
                                {dayOfWeek}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {slots.map(slot => (
                          <ListItem
                            key={slot.id}
                            secondaryAction={
                              <Stack direction="row" spacing={0.5}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(slot)}
                                  disabled={isUpdating || isDeleting}
                                  title="Edit"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDelete(slot)}
                                  disabled={isDeleting}
                                  title="Delete"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            }
                            sx={{ pl: 4 }}
                          >
                            <ListItemText
                              primary={`${formatTime(slot.start_time)} - ${formatTime(slot.end_time)}`}
                            />
                          </ListItem>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
          </Box>

          <Divider />

          {/* Add/Edit Form */}
          {isAddFormOpen ? (
            <Box data-form-section="true">
              <Typography variant="subtitle2" gutterBottom>
                {editingSlot ? 'Edit Availability Slot' : 'Add New Slot'}
              </Typography>
              <Stack spacing={2}>
                {formError && <Alert severity="error">{formError}</Alert>}

                <FormControl fullWidth>
                  <InputLabel>Day</InputLabel>
                  <Select
                    value={day}
                    onChange={e => setDay(e.target.value)}
                    label="Day"
                  >
                    {DAYS_OF_WEEK.map(d => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={startTime}
                  onChange={e => setStartTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={endTime}
                  onChange={e => setEndTime(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />

                <Stack direction="row" spacing={1}>
                  {editingSlot ? (
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleUpdateSlot}
                      disabled={isUpdating}
                    >
                      Save Changes
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddSlot}
                      disabled={isCreating}
                    >
                      Add Slot
                    </Button>
                  )}
                  <Button onClick={handleCloseAddForm}>Cancel</Button>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddForm}
            >
              Add Availability Slot
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailabilityManager;
